import { TITLE_FONT_SIZE } from './tutorialStyles'
import type { TutorialSlide } from '../screens/TutorialScreen'
import { NeonColors, FONTS, neonText } from '../neon-theme'
import { generateSnakeColors, getPlayerColorKey } from '../../game/snakeColors'
import { drawRightFinger } from './fingerHelper'

const SCALE = 2
const LOOP_DURATION = 20

const BASE_SPEED = 1000
const BOOST_SPEED = 2500
const TURN_RATE = 4.2
const INITIAL_WIDTH = 120
const SEGMENT_SPACING = 60
const STARTER_SEG_COUNT = 10
const SPARK_SIZE = 40
const WORLD_SCALE = 0.1

function normalizeRadians(a: number): number {
  let r = a % (Math.PI * 2)
  if (r > Math.PI) r -= Math.PI * 2
  if (r < -Math.PI) r += Math.PI * 2
  return r
}

function hexToStr(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`
}

interface Seg { x: number; z: number }
interface Spark { x: number; z: number; collected: boolean; appearTime: number }
interface DroppedSpark { x: number; z: number; vx: number; vz: number; alpha: number }

interface FingerKeyframe {
  time: number
  x: number // fraction 0..1 of canvas width
  z: number // fraction 0..1 of canvas height
  pressed: boolean
}

export class ControlsSlide implements TutorialSlide {
  private container: HTMLDivElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private canvasW = 0
  private canvasH = 0
  private animFrame = 0
  private startTime = 0
  private lastFrameTime = 0
  private mounted = false
  private currentT = 0

  private headX = 0
  private headZ = 0
  private bearing = -Math.PI / 2
  private desiredHeading = -Math.PI / 2
  private segments: Seg[] = []
  private sparks: Spark[] = []
  private droppedSparks: DroppedSpark[] = []
  private isBoosting = false
  private boostTrailAlpha = 0
  private boostDropTimer = 0
  private camX = 0
  private camZ = 0

  private joyActive = false
  private joyCenterX = 0
  private joyCenterZ = 0
  private joyFingerX = 0
  private joyFingerZ = 0
  private fingerAlpha = 0
  private fingerScale = 1
  private fingerFracX = 1.1
  private fingerFracZ = 0.75

  private snakeBody1 = '#88DD66'
  private snakeBody2 = '#66BB44'
  private snakeHead = '#AAFFAA'
  private snakeGlow = '#88DD66'

  // Finger keyframes: positions as fractions of canvas (0..1)
  // Double tap does NOT create joystick — just visual taps
  private fingerKeyframes: FingerKeyframe[] = [
    // Finger enters from right, approaches screen
    { time: 0.0, x: 1.1, z: 0.75, pressed: false },
    { time: 1.0, x: 0.72, z: 0.72, pressed: false },
    { time: 1.5, x: 0.72, z: 0.72, pressed: true }, // touch down → joystick appears

    // Steer left: finger sweeps left
    { time: 3.0, x: 0.45, z: 0.76, pressed: true },
    { time: 4.5, x: 0.38, z: 0.72, pressed: true },

    // Sweep right (arc, no center return)
    { time: 6.0, x: 0.55, z: 0.68, pressed: true },
    { time: 7.5, x: 0.82, z: 0.72, pressed: true },

    // Sweep up — snake goes straight up
    { time: 9.0, x: 0.72, z: 0.55, pressed: true },
    { time: 10.0, x: 0.72, z: 0.50, pressed: true },

    // Lift finger
    { time: 10.5, x: 0.72, z: 0.48, pressed: false },

    // Pause before double tap
    { time: 12.0, x: 0.72, z: 0.7, pressed: false },

    // DOUBLE TAP — just visual, no joystick creation
    // Tap 1 down
    { time: 12.5, x: 0.72, z: 0.7, pressed: false },
    // Tap 2 (visual only — finger bounces)
    { time: 13.1, x: 0.72, z: 0.7, pressed: false },

    // Boost runs, finger leaves
    { time: 17.0, x: 0.72, z: 0.7, pressed: false },
    { time: 19.0, x: 1.1, z: 0.75, pressed: false },
    { time: 20.0, x: 1.1, z: 0.75, pressed: false },
  ]

  mount(el: HTMLDivElement, _onReady: () => void): void {
    this.container = el
    el.style.padding = '0'

    const savedSeed = localStorage.getItem('star-snake-color-seed')
    const colorSeed = savedSeed !== null ? parseInt(savedSeed, 10) || 0 : 0
    const colors = generateSnakeColors(getPlayerColorKey(colorSeed))
    this.snakeBody1 = hexToStr(colors.body[0])
    this.snakeBody2 = hexToStr(colors.body[1])
    this.snakeHead = hexToStr(colors.head)
    this.snakeGlow = hexToStr(colors.body[0])

    // Canvas fills entire slide — title and text drawn on canvas
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%;'
    el.appendChild(this.canvas)

    this.startTime = performance.now()
    this.lastFrameTime = this.startTime
    this.mounted = true
    this.canvasW = 0; this.canvasH = 0
    this.animFrame = 0
    this.reset()
    window.addEventListener('resize', this.handleResize)
    this.tick()
  }

  unmount(): void {
    this.mounted = false
    if (this.animFrame) cancelAnimationFrame(this.animFrame)
    this.animFrame = 0
    window.removeEventListener('resize', this.handleResize)
    if (this.container) this.container.innerHTML = ''
    this.container = null; this.canvas = null; this.ctx = null
  }

  private handleResize = (): void => { this.resizeCanvas() }

  private resizeCanvas(): void {
    if (!this.canvas) return
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.floor(rect.width) || 320
    const h = Math.floor(rect.height) || 400
    if (w === this.canvasW && h === this.canvasH) return
    this.canvasW = w
    this.canvasH = h
    this.canvas.width = w * SCALE
    this.canvas.height = h * SCALE
    this.ctx = this.canvas.getContext('2d')
  }

  private reset(): void {
    this.headX = 1500; this.headZ = 1500
    this.bearing = -Math.PI / 2; this.desiredHeading = -Math.PI / 2
    this.isBoosting = false; this.boostTrailAlpha = 0; this.boostDropTimer = 0
    this.joyActive = false; this.droppedSparks = []
    this.segments = []
    for (let i = 0; i < STARTER_SEG_COUNT; i++) {
      this.segments.push({ x: this.headX, z: this.headZ + i * SEGMENT_SPACING })
    }
    // Spark 1: on straight path ahead. Snake starts z=1500, moves up at ~1000u/s
    // At t≈1.5 head will be at z≈0, so spark at z=0 gets picked up ~1.5s in
    // Sparks 2,3: exact coordinates from debug log trajectory
    // t=3.0 head=(232,-199), t=8.5 head=(-2500,-1362)
    this.sparks = [
      { x: 1500, z: 0, collected: false, appearTime: 0 },         // straight ahead
      { x: 232, z: -49, collected: false, appearTime: 0 },         // t=3.0 path, +150wu down
      { x: -2350, z: -1362, collected: false, appearTime: 0 },    // t=8.5 path, +150wu right total
    ]
    this.camX = this.headX; this.camZ = this.headZ
    this.fingerFracX = 1.1; this.fingerFracZ = 0.75
    this.fingerAlpha = 0; this.fingerScale = 1
  }

  private tick = (): void => {
    if (!this.mounted || !this.canvas) return
    this.resizeCanvas()
    if (!this.ctx || this.canvasW === 0) { this.animFrame = requestAnimationFrame(this.tick); return }
    const now = performance.now()
    const elapsed = (now - this.startTime) / 1000
    const t = elapsed % LOOP_DURATION
    this.currentT = t
    if (t < 0.05 && elapsed > 0.5) this.reset()

    const frameDt = Math.min(0.1, (now - this.lastFrameTime) / 1000)
    this.lastFrameTime = now

    this.updateFinger(t)
    this.updateJoystick(t)

    let remaining = frameDt
    while (remaining > 0) {
      const step = Math.min(remaining, 1 / 30)
      this.physicsStep(t, step)
      remaining -= step
    }

    for (const sp of this.droppedSparks) {
      sp.x += sp.vx * frameDt; sp.z += sp.vz * frameDt
      sp.vx *= 0.95; sp.vz *= 0.95
      sp.alpha = Math.max(0, sp.alpha - frameDt * 0.5)
    }
    this.droppedSparks = this.droppedSparks.filter(s => s.alpha > 0.01)

    this.camX += (this.headX - this.camX) * 0.08
    this.camZ += (this.headZ - this.camZ) * 0.08

    this.draw(t)
    this.animFrame = requestAnimationFrame(this.tick)
  }

  private updateFinger(t: number): void {
    const kf = this.fingerKeyframes
    let a = kf[0]!, b = kf[0]!
    for (let i = 0; i < kf.length - 1; i++) {
      if (t >= kf[i]!.time && t < kf[i + 1]!.time) { a = kf[i]!; b = kf[i + 1]!; break }
      if (i === kf.length - 2) { a = kf[i + 1]!; b = kf[i + 1]! }
    }
    const range = b.time - a.time
    const p = range > 0.001 ? Math.min(1, (t - a.time) / range) : 1
    const sp = p * p * (3 - 2 * p)
    this.fingerFracX = a.x + (b.x - a.x) * sp
    this.fingerFracZ = a.z + (b.z - a.z) * sp
    const onScreen = this.fingerFracX < 1.05
    this.fingerAlpha = onScreen ? Math.min(1, this.fingerAlpha + 0.05) : Math.max(0, this.fingerAlpha - 0.05)

    // Double-tap bounce effect (12.5–13.1s) — just visual finger bounce, no joystick
    if (t >= 12.3 && t < 12.6) {
      this.fingerScale = 0.8
    } else if (t >= 12.6 && t < 12.8) {
      this.fingerScale = 1.0
    } else if (t >= 12.8 && t < 13.0) {
      this.fingerScale = 0.8
    } else if (t >= 13.0 && t < 13.2) {
      this.fingerScale = 1.0
    } else {
      const isPressed = a.pressed
      this.fingerScale = isPressed ? 0.85 : 1.0
    }
  }

  private updateJoystick(t: number): void {
    const kf = this.fingerKeyframes
    let pressed = false
    for (let i = 0; i < kf.length - 1; i++) {
      if (t >= kf[i]!.time && t < kf[i + 1]!.time) { pressed = kf[i]!.pressed; break }
    }

    if (pressed && !this.joyActive) {
      this.joyActive = true
      this.joyCenterX = this.fingerFracX * this.canvasW
      this.joyCenterZ = this.fingerFracZ * this.canvasH
    } else if (!pressed) {
      this.joyActive = false
    }

    if (this.joyActive) {
      this.joyFingerX = this.fingerFracX * this.canvasW
      this.joyFingerZ = this.fingerFracZ * this.canvasH
      const dx = this.joyFingerX - this.joyCenterX
      const dz = this.joyFingerZ - this.joyCenterZ
      if (dx * dx + dz * dz > 9) {
        this.desiredHeading = Math.atan2(dz, dx)
      }
    }

    // Boost: triggered at 13.2s, no joystick involved
    if (t >= 13.2 && t < 17) {
      if (!this.isBoosting) this.isBoosting = true
      this.boostTrailAlpha = Math.min(1, this.boostTrailAlpha + 0.04)
    } else {
      this.isBoosting = false
      this.boostTrailAlpha *= 0.95
    }
  }

  private physicsStep(t: number, dt: number): void {
    const maxDelta = TURN_RATE * dt
    const diff = normalizeRadians(this.desiredHeading - this.bearing)
    if (Math.abs(diff) <= maxDelta) this.bearing = normalizeRadians(this.desiredHeading)
    else this.bearing = normalizeRadians(this.bearing + Math.sign(diff) * maxDelta)

    const speed = this.isBoosting ? BOOST_SPEED : BASE_SPEED
    this.headX += Math.cos(this.bearing) * speed * dt
    this.headZ += Math.sin(this.bearing) * speed * dt

    this.segments[0] = { x: this.headX, z: this.headZ }
    for (let i = 1; i < this.segments.length; i++) {
      const prev = this.segments[i - 1]!
      const cur = this.segments[i]!
      const dx = cur.x - prev.x, dz = cur.z - prev.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist > SEGMENT_SPACING) {
        const r = SEGMENT_SPACING / dist
        cur.x = prev.x + dx * r; cur.z = prev.z + dz * r
      }
    }

    // Boost: drop spark from tail once per second
    if (this.isBoosting && this.segments.length > STARTER_SEG_COUNT) {
      this.boostDropTimer += dt
      if (this.boostDropTimer >= 1.0) {
        this.boostDropTimer -= 1.0
        const tail = this.segments[this.segments.length - 1]!
        this.segments.pop()
        const angle = Math.random() * Math.PI * 2
        this.droppedSparks.push({
          x: tail.x, z: tail.z,
          vx: Math.cos(angle) * 80, vz: Math.sin(angle) * 80,
          alpha: 1,
        })
      }
    }

    // Collect sparks (only if appeared)
    const cr = INITIAL_WIDTH * 0.8
    const loopT = (performance.now() - this.startTime) / 1000 % LOOP_DURATION
    for (const sp of this.sparks) {
      if (sp.collected || loopT < sp.appearTime) continue
      const dx = this.headX - sp.x, dz = this.headZ - sp.z
      if (dx * dx + dz * dz < cr * cr) {
        sp.collected = true
        const tail = this.segments[this.segments.length - 1]!
        this.segments.push({ x: tail.x, z: tail.z })
      }
    }
  }

  // Snake renders at visual center of the "game area" (below title, above bottom text)
  private get gameAreaCenterX(): number { return this.canvasW * SCALE / 2 }
  private get gameAreaCenterZ(): number { return this.canvasH * SCALE * 0.45 }

  private toSX(wx: number): number { return (wx - this.camX) * WORLD_SCALE * SCALE + this.gameAreaCenterX }
  private toSZ(wz: number): number { return (wz - this.camZ) * WORLD_SCALE * SCALE + this.gameAreaCenterZ }

  private draw(t: number): void {
    const ctx = this.ctx!
    const w = this.canvasW * SCALE
    const h = this.canvasH * SCALE
    if (w === 0 || h === 0) return
    const s = SCALE

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = NeonColors.bgDark
    ctx.fillRect(0, 0, w, h)

    // Grid with battleship-style labels
    const gs = 300
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 1
    const gsX = Math.floor((this.camX - this.canvasW / WORLD_SCALE / 2) / gs) * gs
    const gsZ = Math.floor((this.camZ - this.canvasH / WORLD_SCALE / 2) / gs) * gs
    for (let gx = gsX; gx < this.camX + this.canvasW / WORLD_SCALE; gx += gs) {
      const sx = this.toSX(gx); ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, h); ctx.stroke()
    }
    for (let gz = gsZ; gz < this.camZ + this.canvasH / WORLD_SCALE; gz += gs) {
      const sz = this.toSZ(gz); ctx.beginPath(); ctx.moveTo(0, sz); ctx.lineTo(w, sz); ctx.stroke()
    }

    // Edge fade (all 4 sides)
    const fadeSize = 30 * s
    // Top fade
    const topGrad = ctx.createLinearGradient(0, 0, 0, fadeSize)
    topGrad.addColorStop(0, NeonColors.bgDark); topGrad.addColorStop(1, 'rgba(0,2,10,0)')
    ctx.fillStyle = topGrad; ctx.fillRect(0, 0, w, fadeSize)
    // Bottom fade
    const botGrad = ctx.createLinearGradient(0, h - fadeSize, 0, h)
    botGrad.addColorStop(0, 'rgba(0,2,10,0)'); botGrad.addColorStop(1, NeonColors.bgDark)
    ctx.fillStyle = botGrad; ctx.fillRect(0, h - fadeSize, w, fadeSize)
    // Left fade
    const leftGrad = ctx.createLinearGradient(0, 0, fadeSize, 0)
    leftGrad.addColorStop(0, NeonColors.bgDark); leftGrad.addColorStop(1, 'rgba(0,2,10,0)')
    ctx.fillStyle = leftGrad; ctx.fillRect(0, 0, fadeSize, h)
    // Right fade
    const rightGrad = ctx.createLinearGradient(w - fadeSize, 0, w, 0)
    rightGrad.addColorStop(0, 'rgba(0,2,10,0)'); rightGrad.addColorStop(1, NeonColors.bgDark)
    ctx.fillStyle = rightGrad; ctx.fillRect(w - fadeSize, 0, fadeSize, h)

    // Collectible sparks (only visible after appearTime)
    for (const sp of this.sparks) {
      if (sp.collected || t < sp.appearTime) continue
      const sx = this.toSX(sp.x), sz = this.toSZ(sp.z)
      const r = SPARK_SIZE * WORLD_SCALE * s * 0.5
      const pulse = 0.6 + Math.sin(performance.now() * 0.004 + sp.x * 0.01) * 0.3
      ctx.beginPath(); ctx.arc(sx, sz, r, 0, Math.PI * 2)
      ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = pulse * 0.9
      ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = r * 2; ctx.fill()
      ctx.shadowBlur = 0; ctx.globalAlpha = 1
    }

    // Dropped sparks (from boost)
    for (const sp of this.droppedSparks) {
      const sx = this.toSX(sp.x), sz = this.toSZ(sp.z)
      const r = SPARK_SIZE * WORLD_SCALE * s * 0.4
      ctx.beginPath(); ctx.arc(sx, sz, r, 0, Math.PI * 2)
      ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = sp.alpha * 0.8
      ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = r; ctx.fill()
      ctx.shadowBlur = 0
    }
    ctx.globalAlpha = 1

    // Snake
    const segs = this.segments
    const bodyR = INITIAL_WIDTH * WORLD_SCALE * s * 0.5
    const headR = bodyR * 1.3

    // Glow
    for (let i = Math.min(segs.length - 1, 20); i >= 0; i--) {
      const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR * 1.5, 0, Math.PI * 2)
      ctx.fillStyle = this.snakeGlow; ctx.globalAlpha = 0.045; ctx.fill()
    }
    ctx.globalAlpha = 1

    // Body
    for (let i = segs.length - 1; i >= 1; i--) {
      const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR, 0, Math.PI * 2)
      ctx.fillStyle = i % 2 === 0 ? this.snakeBody1 : this.snakeBody2
      ctx.globalAlpha = i < 3 ? 0.5 + i * 0.15 : 0.94; ctx.fill()
    }
    ctx.globalAlpha = 1

    // Boost trail
    if (this.boostTrailAlpha > 0.01 && this.isBoosting) {
      for (let i = 1; i < Math.min(8, segs.length); i++) {
        const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
        ctx.beginPath(); ctx.arc(sx, sz, bodyR * 0.6 * (1 - i * 0.08), 0, Math.PI * 2)
        ctx.fillStyle = this.snakeGlow; ctx.globalAlpha = this.boostTrailAlpha * (0.2 - i * 0.02); ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    // Head
    {
      const sx = this.toSX(this.headX), sz = this.toSZ(this.headZ)
      ctx.beginPath(); ctx.arc(sx, sz, headR, 0, Math.PI * 2)
      ctx.fillStyle = this.snakeHead
      ctx.shadowColor = this.snakeGlow; ctx.shadowBlur = headR; ctx.fill(); ctx.shadowBlur = 0
      this.drawEyes(ctx, sx, sz, headR, this.bearing)
    }

    // Joystick (only when finger is pressing, NOT during double-tap)
    if (this.joyActive) {
      const jcx = this.joyCenterX * s, jcz = this.joyCenterZ * s
      const jr = 42 * s
      ctx.beginPath(); ctx.arc(jcx, jcz, jr, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.lineWidth = 1.5 * s; ctx.stroke()
      const fx = this.joyFingerX * s, fz = this.joyFingerZ * s
      const dx = fx - jcx, dz = fz - jcz, mag = Math.sqrt(dx * dx + dz * dz)
      const cm = Math.min(mag, jr * 0.8)
      const stX = mag > 0.1 ? jcx + (dx / mag) * cm : jcx
      const stZ = mag > 0.1 ? jcz + (dz / mag) * cm : jcz
      ctx.beginPath(); ctx.arc(stX, stZ, 16 * s, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(255,255,255,0.2)'; ctx.fill()
    }

    // Finger (SVG)
    if (this.fingerAlpha > 0.01) {
      const fx = this.fingerFracX * this.canvasW * s
      const fz = this.fingerFracZ * this.canvasH * s
      drawRightFinger(ctx, fx, fz, 36 * s, this.fingerAlpha * 0.7, this.fingerScale)
    }

    // Title "CONTROLS" on canvas — below notch area
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'
    ctx.font = `bold ${TITLE_FONT_SIZE}px ${FONTS.title}`
    ctx.fillStyle = NeonColors.primary
    ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 12 * s
    ctx.globalAlpha = 0.95
    ctx.fillText('CONTROLS', w / 2, 53 * s)
    ctx.shadowBlur = 0; ctx.globalAlpha = 1

    // Bottom hint text on canvas — above bottom fade
    this.drawBottomText(ctx, t, w, h, s)

    // Fade at loop end
    if (t > 18.5) {
      ctx.fillStyle = NeonColors.bgDark
      ctx.globalAlpha = Math.min(1, (t - 18.5) / 1.5)
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
    }
  }

  private drawBottomText(ctx: CanvasRenderingContext2D, t: number, _w: number, _h: number, s: number): void {
    let text = ''
    let sub = ''

    if (t < 1.5) {
      text = 'Touch anywhere'
      sub = 'Place your finger on the screen'
    } else if (t < 10.5) {
      text = 'Drag to steer'
      sub = 'Hold and sweep to change direction'
    } else if (t < 13.2) {
      text = 'Double-tap'
      sub = 'Tap twice quickly to boost'
    } else if (t < 17) {
      text = 'Boosting!'
      sub = 'Burns your sparks for speed'
    }

    if (!text) return

    // Text block: left side, same vertical zone as joystick
    const tx = 0.08 * this.canvasW * s
    const tz = 0.72 * this.canvasH * s

    ctx.textAlign = 'left'

    // Main text (bold, shorter)
    ctx.textBaseline = 'bottom'
    ctx.font = `bold ${Math.round(11 * 1.1 * s)}px ${FONTS.body}`
    ctx.fillStyle = NeonColors.textBright; ctx.globalAlpha = 0.9
    ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 4 * s
    ctx.fillText(text, tx, tz - 1 * s)
    ctx.shadowBlur = 0

    // Sub text (longer, right below)
    ctx.textBaseline = 'top'
    ctx.font = `${Math.round(8 * 1.1 * s)}px ${FONTS.body}`
    ctx.fillStyle = NeonColors.textMuted; ctx.globalAlpha = 0.55
    ctx.fillText(sub, tx, tz + 3 * s)

    ctx.globalAlpha = 1
  }

  private drawEyes(ctx: CanvasRenderingContext2D, hx: number, hz: number, headR: number, bearing: number): void {
    const eco = Math.cos(bearing), esi = Math.sin(bearing)
    const off = headR * 0.55, r = headR * 0.28
    for (const side of [-1, 1]) {
      const ex = hx + eco * off * 0.4 - esi * off * side
      const ez = hz + esi * off * 0.4 + eco * off * side
      ctx.beginPath(); ctx.arc(ex, ez, r, 0, Math.PI * 2); ctx.fillStyle = '#FFFFFF'; ctx.fill()
      ctx.beginPath(); ctx.arc(ex + eco * r * 0.4, ez + esi * r * 0.4, r * 0.4, 0, Math.PI * 2)
      ctx.fillStyle = '#000000'; ctx.fill()
    }
  }
}
